import { useState, useCallback, useEffect } from 'react';
import { CartItem, Customer } from '../QuickSaleScreen';
import { PendingSale } from '../components/modals/PendingSalesModal';

const PENDING_SALES_KEY = 'quickSalePendingSales';

interface UseQuickSalePendingProps {
  cart: CartItem[];
  selectedCustomer: Customer | null;
  clearCart: () => void;
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  setSelectedCustomer: React.Dispatch<React.SetStateAction<Customer | null>>;
  showSnackbar: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
}

interface UseQuickSalePendingReturn {
  // State
  pendingSales: PendingSale[];
  isPendingSalesModalOpen: boolean;
  
  // Actions
  setIsPendingSalesModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleHoldSale: () => void;
  handleOpenPendingSalesModal: () => void;
  handleRestoreSale: (saleId: number) => void;
  handleDeleteSale: (saleId: number) => void;
}

export const useQuickSalePending = ({
  cart,
  selectedCustomer,
  clearCart,
  setCart,
  setSelectedCustomer,
  showSnackbar,
}: UseQuickSalePendingProps): UseQuickSalePendingReturn => {
  const [pendingSales, setPendingSales] = useState<PendingSale[]>([]);
  const [isPendingSalesModalOpen, setIsPendingSalesModalOpen] = useState(false);

  // Component ilk yüklendiğinde localStorage'dan Bekleyen Satışları yükle
  useEffect(() => {
    try {
      const savedPendingSales = localStorage.getItem(PENDING_SALES_KEY);
      if (savedPendingSales) {
        const parsedSales: PendingSale[] = JSON.parse(savedPendingSales);
        if (Array.isArray(parsedSales)) {
          setPendingSales(parsedSales);
        }
      }
    } catch (error) {
      console.error('Bekleyen satışlar yüklenirken hata:', error);
    }
  }, []);

  // Bekleyen satışları localStorage'a kaydetme
  const savePendingSalesToLocalStorage = useCallback((sales: PendingSale[]) => {
    try {
      localStorage.setItem(PENDING_SALES_KEY, JSON.stringify(sales));
    } catch (error) {
      console.error('Bekleyen satışlar kaydedilirken hata:', error);
    }
  }, []);

  // Satışı beklemeye alma
  const handleHoldSale = useCallback(() => {
    if (cart.length === 0) {
      showSnackbar('Sepette ürün olmadan beklemeye alamazsınız.', 'warning');
      return;
    }

    // Yeni bekleyen satış oluştur
    const newPendingSale: PendingSale = {
      id: Date.now(), // Benzersiz ID
      timestamp: Date.now(),
      totalAmount: cart.reduce((sum, item) => {
        const itemTotal = item.price * item.quantity;
        const vatAmount = itemTotal * ((item.vatRate || 0) / 100);
        return sum + itemTotal + vatAmount;
      }, 0),
      cartItems: [...cart],
      selectedCustomer: selectedCustomer ? { ...selectedCustomer } : null,
    };

    const updatedPendingSales = [...pendingSales, newPendingSale];
    setPendingSales(updatedPendingSales);
    savePendingSalesToLocalStorage(updatedPendingSales);

    // Sepeti temizle
    clearCart();
    
    showSnackbar('Satış beklemeye alındı.', 'success');
  }, [cart, selectedCustomer, pendingSales, clearCart, savePendingSalesToLocalStorage, showSnackbar]);

  // Bekleyen satışlar modalını açma
  const handleOpenPendingSalesModal = useCallback(() => {
    if (pendingSales.length === 0) {
      showSnackbar('Bekleyen satış bulunmamaktadır.', 'info');
      return;
    }
    setIsPendingSalesModalOpen(true);
  }, [pendingSales.length, showSnackbar]);

  // Bekleyen satışı geri yükleme
  const handleRestoreSale = useCallback((saleId: number) => {
    const saleToRestore = pendingSales.find((sale) => sale.id === saleId);
    
    if (!saleToRestore) {
      showSnackbar('Satış bulunamadı.', 'error');
      return;
    }

    // Eğer sepette ürün varsa kullanıcıya uyarı ver (Bu logic QuickSaleScreen içinde yapılabilir)
    if (cart.length > 0) {
      // Modal içinde onay alınması gerekebilir, şimdilik direkt yüklüyoruz
      console.warn('Sepette ürün var, üzerine yazılacak!');
    }

    // Sepeti ve müşteriyi geri yükle
    setCart(saleToRestore.cartItems);
    setSelectedCustomer(saleToRestore.selectedCustomer);

    // Bu satışı bekleyenlerden kaldır
    const updatedPendingSales = pendingSales.filter((sale) => sale.id !== saleId);
    setPendingSales(updatedPendingSales);
    savePendingSalesToLocalStorage(updatedPendingSales);

    // Modalı kapat
    setIsPendingSalesModalOpen(false);
    
    showSnackbar('Satış geri yüklendi.', 'success');
  }, [
    pendingSales,
    cart.length,
    setCart,
    setSelectedCustomer,
    savePendingSalesToLocalStorage,
    showSnackbar,
  ]);

  // Bekleyen satışı silme
  const handleDeleteSale = useCallback((saleId: number) => {
    const updatedPendingSales = pendingSales.filter((sale) => sale.id !== saleId);
    setPendingSales(updatedPendingSales);
    savePendingSalesToLocalStorage(updatedPendingSales);
    
    showSnackbar('Bekleyen satış silindi.', 'info');
  }, [pendingSales, savePendingSalesToLocalStorage, showSnackbar]);

  return {
    pendingSales,
    isPendingSalesModalOpen,
    setIsPendingSalesModalOpen,
    handleHoldSale,
    handleOpenPendingSalesModal,
    handleRestoreSale,
    handleDeleteSale,
  };
};

